import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../components/common/callout.component';
import { ChipTone } from '../components/common/guarantee-vocabulary';
import { OptionPickerComponent, PickerOption } from '../components/common/option-picker.component';
import { DIALOG_CLASSES } from '../components/ui/ui.classes';

/**
 * Which act is being confirmed, so the dialog reads as that act rather than as a generic warning.
 * Drawn from the module's own tone vocabulary rather than a list of its own — a destructive act is
 * the same red here as on the chip that reports it afterwards.
 */
export type ConfirmTone = Extract<ChipTone, 'error' | 'success'>;

/** The committing button's color by tone, shared with the decision form so the same act is the same red. */
export const CONFIRM_CLASS: Record<ConfirmTone, string> = {
  error: 'bg-error-600 hover:bg-error-700',
  success: 'bg-success-600 hover:bg-success-700',
};

const ICON_CLASS: Record<ConfirmTone, string> = {
  error: 'text-error-600',
  success: 'text-success-600',
};

/** What grounds the act is recorded on, where it takes any. Absent, the dialog only asks. */
export interface ConfirmReasonConfig {
  labelKey: string;
  placeholderKey: string;
  /** Shown while unanswered, and as the refusal on the disabled confirming button. */
  requiredKey: string;
  /**
   * The grounds offered, which belong to the record being acted on rather than to this dialog. A
   * prescription is removed for prescription reasons and a case for case reasons, and a single list
   * shared between them can only be right for one of the two.
   */
  options: string[];
}

export interface ConfirmDialogData {
  titleKey: string;
  /** What the act is about, named so the question is about this record rather than about the act. */
  subject: string;
  /** What taking it actually costs — a line and a whole care plan are not the same loss. */
  warningKey: string;
  confirmKey: string;
  icon: string;
  tone: ConfirmTone;
  reason?: ConfirmReasonConfig;
}

/** The ground the act was taken on, empty where the dialog asked for none. */
export interface ConfirmDialogResult {
  reason: string;
}

/**
 * Asked before an act the record keeps: it names what is about to happen, what it costs, and — where
 * the act is reported on — the ground it is taken on.
 *
 * One dialog for both because they are one question. A deletion and a closed review differ in what
 * they cost and whether a ground is owed, not in what is being asked, and two components would drift
 * into two ways of asking it.
 */
export function openConfirm(dialog: MatDialog, data: ConfirmDialogData): Observable<ConfirmDialogResult | undefined> {
  return dialog
    .open<ConfirmDialogComponent, ConfirmDialogData, ConfirmDialogResult>(ConfirmDialogComponent, {
      width: '90vw',
      maxWidth: '440px',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    })
    .afterClosed()
    .pipe(take(1));
}

/**
 * Deletion, asked identically wherever it is asked for. A named front door rather than a shape each
 * caller assembles: the ground is what makes a removal reviewable afterwards, so no call site is in
 * a position to leave it out.
 */
export function openDeleteConfirm(
  dialog: MatDialog,
  data: Pick<ConfirmDialogData, 'subject' | 'titleKey' | 'warningKey'> & { reasons: string[] }
): Observable<ConfirmDialogResult | undefined> {
  const { reasons, ...question } = data;

  return openConfirm(dialog, {
    ...question,
    confirmKey: 'guaranteeV2.action.confirmDelete',
    icon: 'delete',
    tone: 'error',
    reason: {
      labelKey: 'guaranteeV2.label.deleteReason',
      placeholderKey: 'guaranteeV2.message.deleteReasonPlaceholder',
      requiredKey: 'guaranteeV2.message.deleteReasonRequired',
      options: reasons,
    },
  });
}

@Component({
  selector: 'guarantees-confirm-dialog',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe, CalloutComponent, OptionPickerComponent],
  templateUrl: './confirm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  private readonly dialogRef = inject(MatDialogRef<ConfirmDialogComponent, ConfirmDialogResult>);

  protected readonly data = inject<ConfirmDialogData>(MAT_DIALOG_DATA);

  protected readonly confirmClass = CONFIRM_CLASS[this.data.tone];
  protected readonly iconClass = ICON_CLASS[this.data.tone];

  /** Whatever grounds the caller offers — this dialog asks the question, it does not own the answers. */
  protected readonly reasons: PickerOption[] = (this.data.reason?.options ?? []).map(value => ({ value }));
  protected readonly reason = signal('');

  protected readonly canConfirm = computed(() => !this.data.reason || !!this.reason().trim());

  protected confirm(): void {
    if (!this.canConfirm()) {
      return;
    }

    this.dialogRef.close({ reason: this.reason().trim() });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
